import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { MAX_AGENT_QUERY_LENGTH, type ProjectType } from '@/api-types';
import { useAuthGuard } from './useAuthGuard';

export interface CreateAppAttachment {
	url?: string;
	filename?: string;
	mimeType?: string;
}

export interface CreateAppOptions {
	attachments?: CreateAppAttachment[];
	onComplete?: () => void;
}

// Builds the chat URL and routes / auth-gates the user on submit.
export function useCreateApp() {
	const navigate = useNavigate();
	const { requireAuth } = useAuthGuard();

	return useCallback(
		(query: string, mode: ProjectType, options: CreateAppOptions = {}) => {
			if (query.length > MAX_AGENT_QUERY_LENGTH) {
				toast.error(
					`Prompt too large (${query.length} characters). Maximum allowed is ${MAX_AGENT_QUERY_LENGTH} characters.`,
				);
				return;
			}

			const encodedQuery = encodeURIComponent(query);
			const encodedMode = encodeURIComponent(mode);
			const attachments = options.attachments ?? [];
			const attachmentParam =
				attachments.length > 0
					? `&attachments=${encodeURIComponent(JSON.stringify(attachments))}`
					: '';
			const intendedUrl = `/chat/new?query=${encodedQuery}&projectType=${encodedMode}${attachmentParam}`;

			if (
				!requireAuth({
					requireFullAuth: true,
					actionContext: 'to create applications',
					intendedUrl,
				})
			) {
				return;
			}

			navigate(intendedUrl);
			options.onComplete?.();
		},
		[navigate, requireAuth],
	);
}
